import { useEffect, useState } from 'react';
import { useGetApiV1ClassroomdisciplineSharedClassStudents } from '@/shared/api/generated';

/** Opção de turma para o Autocomplete. */
export interface ClassOption {
  readonly id: string;
  readonly name: string;
}

/**
 * Busca de turmas (class-students) por texto, com debounce de 1 s e mínimo de 3 caracteres.
 *
 * Consome o endpoint compartilhado `GET /api/v1/classroomdiscipline/shared/class-students`
 * via hook gerado pelo Kubb, mapeando `DropDownListViewModel` (`key`/`value`) para as opções
 * do Autocomplete. Espelha o padrão de `useClassStudentsSearchDropdown` de student-management.
 *
 * @param filter - Texto digitado no campo de busca (default `''`).
 * @param courseId - Filtra por curso, quando informado.
 * @returns `{ classes, isLoading, isQuerySuccess }`.
 */
export function useClassSearchDropdown(filter = '', courseId?: number) {
  const [debouncedFilter, setDebouncedFilter] = useState('');

  useEffect(() => {
    if (filter === '') {
      setDebouncedFilter('');
      return;
    }
    const timer = setTimeout(() => setDebouncedFilter(filter), 1000);
    return () => clearTimeout(timer);
  }, [filter]);

  const isReady = debouncedFilter.length >= 3;

  const { data, isLoading, isSuccess } = useGetApiV1ClassroomdisciplineSharedClassStudents(
    isReady
      ? { Search: debouncedFilter, ...(courseId !== undefined ? { CourseId: courseId } : {}) }
      : undefined,
    { query: { enabled: isReady } },
  );

  const classes: readonly ClassOption[] = (data ?? []).map(item => ({
    id: String(item.key ?? ''),
    name: item.value ?? '',
  }));

  return { classes, isLoading, isQuerySuccess: isSuccess } as const;
}
